/* global axios */
import ApiClient from './ApiClient';

class ContactCrmAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  // CRM Data
  getCrmData(contactId) {
    return axios.get(`${this.url}/${contactId}/crm`);
  }

  updateCrmData(contactId, data) {
    return axios.patch(`${this.url}/${contactId}/crm`, { contact: data });
  }

  getCrmSummary(contactId) {
    return axios.get(`${this.url}/${contactId}/crm/summary`);
  }

  recalculateMetrics(contactId) {
    return axios.post(`${this.url}/${contactId}/crm/recalculate_metrics`);
  }

  // Addresses
  getAddresses(contactId) {
    return axios.get(`${this.url}/${contactId}/addresses`);
  }

  createAddress(contactId, data) {
    return axios.post(`${this.url}/${contactId}/addresses`, { address: data });
  }

  updateAddress(contactId, addressId, data) {
    return axios.patch(`${this.url}/${contactId}/addresses/${addressId}`, {
      address: data,
    });
  }

  deleteAddress(contactId, addressId) {
    return axios.delete(`${this.url}/${contactId}/addresses/${addressId}`);
  }

  setPrimaryAddress(contactId, addressId) {
    return axios.post(
      `${this.url}/${contactId}/addresses/${addressId}/set_primary`
    );
  }

  // Timeline Events
  getTimelineEvents(contactId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.event_type) queryParams.append('event_type', params.event_type);
    if (params.pinned) queryParams.append('pinned', params.pinned);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);

    const query = queryParams.toString();
    return axios.get(
      `${this.url}/${contactId}/timeline_events${query ? `?${query}` : ''}`
    );
  }

  createTimelineEvent(contactId, data) {
    return axios.post(`${this.url}/${contactId}/timeline_events`, {
      timeline_event: data,
    });
  }

  updateTimelineEvent(contactId, eventId, data) {
    return axios.patch(`${this.url}/${contactId}/timeline_events/${eventId}`, {
      timeline_event: data,
    });
  }

  deleteTimelineEvent(contactId, eventId) {
    return axios.delete(`${this.url}/${contactId}/timeline_events/${eventId}`);
  }

  togglePinTimelineEvent(contactId, eventId) {
    return axios.post(
      `${this.url}/${contactId}/timeline_events/${eventId}/toggle_pin`
    );
  }

  // Social Profiles
  getSocialProfiles(contactId) {
    return axios.get(`${this.url}/${contactId}/social_profiles`);
  }

  createSocialProfile(contactId, data) {
    return axios.post(`${this.url}/${contactId}/social_profiles`, {
      social_profile: data,
    });
  }

  updateSocialProfile(contactId, profileId, data) {
    return axios.patch(
      `${this.url}/${contactId}/social_profiles/${profileId}`,
      { social_profile: data }
    );
  }

  deleteSocialProfile(contactId, profileId) {
    return axios.delete(
      `${this.url}/${contactId}/social_profiles/${profileId}`
    );
  }

  // Documents
  getDocuments(contactId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.document_type)
      queryParams.append('document_type', params.document_type);
    if (params.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    return axios.get(
      `${this.url}/${contactId}/documents${query ? `?${query}` : ''}`
    );
  }

  createDocument(contactId, data) {
    return axios.post(`${this.url}/${contactId}/documents`, { document: data });
  }

  updateDocument(contactId, documentId, data) {
    return axios.patch(`${this.url}/${contactId}/documents/${documentId}`, {
      document: data,
    });
  }

  deleteDocument(contactId, documentId) {
    return axios.delete(`${this.url}/${contactId}/documents/${documentId}`);
  }

  verifyDocument(contactId, documentId) {
    return axios.post(
      `${this.url}/${contactId}/documents/${documentId}/verify`
    );
  }

  rejectDocument(contactId, documentId, reason) {
    return axios.post(
      `${this.url}/${contactId}/documents/${documentId}/reject`,
      { reason }
    );
  }

  uploadDocument(contactId, file, documentData) {
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(documentData).forEach(key => {
      formData.append(`document[${key}]`, documentData[key]);
    });

    return axios.post(`${this.url}/${contactId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  downloadDocument(contactId, documentId) {
    return axios.get(
      `${this.url}/${contactId}/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
  }
}

export default new ContactCrmAPI();
